import React, { useState, useEffect } from 'react';
import { FaPaperPlane, FaEnvelope, FaEnvelopeOpenText, FaUsers, FaCopy, FaCheck, FaShoppingCart, FaArrowRight, FaSpinner, FaGlobe, FaWhatsapp } from 'react-icons/fa';
import { Link } from 'react-router-dom';
import { Modal, Button, Form, Table } from 'react-bootstrap';
import { toast } from 'react-toastify';
import { handleApiResponse, showApiMessage } from '../../modals/global/ApiMessageModal/ApiMessageModal';
import { useAuthContext } from '../../context/AuthContext';
import { guestApi } from '../../api/guestList';
import smsApi from '../../api/messaging/smsApi';
import { eventApi } from '../../api/events';
import { authApi } from '../../api/auth/authApi';

import './Dashboard.css';
import Wageni from '../wageni/Wageni';
import QuickReportModal from '../../modals/dashboard/QuickReportModal/QuickReportModal';
import KumbushaMichangoModal from '../../modals/wageniModal/KumbushaMichangoModal';
import TumaTaarifaModal from '../../modals/wageniModal/TumaTaarifaModal';
import OnboardingModal from '../../modals/onboarding/OnboardingModal';
import CreateEventModal from '../../modals/events/CreateEventModal';

const Dashboard = () => {
  const { events, currentEvent, isAuthenticated, switchEvent } = useAuthContext();

  // State for dashboard data
  const [dashboardData, setDashboardData] = useState({
    totalPledges: 0,
    totalPledgesCount: 0,
    totalFunds: 0,
    totalFundsCount: 0,
    totalBudget: 0,
    totalExpenditures: 0,
    smsCredits: 0,
    smsUsed: 0,
    whatsappCredits: 0,
    whatsappUsed: 0,
    recentGuests: [],
    recentPledges: [],
    loading: true,
    error: null
  });

  // Modal states
  const [showMichangoModal, setShowMichangoModal] = useState(false);
  const [showTumaTaarifaModal, setShowTumaTaarifaModal] = useState(false);
  const [showQuickReportModal, setShowQuickReportModal] = useState(false);
  const [showOnboarding, setShowOnboarding] = useState(false);
  const [showChangePasswordModal, setShowChangePasswordModal] = useState(false);
  const [showEventSelectorModal, setShowEventSelectorModal] = useState(false);
  const [showCreateEventModal, setShowCreateEventModal] = useState(false);

  // New Password State
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [passwordLoading, setPasswordLoading] = useState(false);
  const [passwordError, setPasswordError] = useState('');

  const [guestsData, setGuestsData] = useState([]);
  const [guestsLoading, setGuestsLoading] = useState(false);
  const [guestStats, setGuestStats] = useState({
    totalGuests: 0,
    walioahidi: 0,
    waliochangia: 0,
    walioalikwa: 0,
  });
  const [websiteUrl, setWebsiteUrl] = useState('');

  // Fetch all dashboard data
  useEffect(() => {
    // Check if change password modal is required
    if (localStorage.getItem('show_change_password_modal') === 'true') {
      setShowChangePasswordModal(true);
    } else if (localStorage.getItem('show_select_event_modal') === 'true') {
      setShowEventSelectorModal(true);
      localStorage.removeItem('show_select_event_modal');
    }

    // Check if new user for onboarding
    if (localStorage.getItem('isNewUserOnboarding') === 'true') {
      setShowOnboarding(true);
    }

    if (isAuthenticated && currentEvent) {
      fetchDashboardData();
    } else if (isAuthenticated && (!currentEvent || events.length === 0)) {
      // No events found
      setDashboardData(prev => ({
        ...prev,
        loading: false,
        error: null
      }));
    }
  }, [isAuthenticated, currentEvent, events.length]);

  const handleSaveNewPassword = async (e) => {
    e.preventDefault();
    if (newPassword.length < 6) {
      setPasswordError('Password lazima iwe na herufi 6 au zaidi');
      return;
    }
    if (newPassword !== confirmPassword) {
      setPasswordError('Password hazifanani');
      return;
    }

    setPasswordLoading(true);
    setPasswordError('');

    try {
      const authCode = localStorage.getItem('temp_auth_code');
      const mobile = localStorage.getItem('reset_mobile');

      const response = await authApi.resetPassword({
        authorization_code: authCode,
        new_password: newPassword,
        mobile: mobile
      });

      if (handleApiResponse(response)) {
        localStorage.removeItem('show_change_password_modal');
        localStorage.removeItem('temp_auth_code');
        localStorage.removeItem('reset_mobile');
        setShowChangePasswordModal(false);
        // Automatically show event choice modal inside dashboard
        setShowEventSelectorModal(true);
      } else {
        setPasswordError(response?.message || 'Hitilafu katika kuhifadhi password mpya');
      }
    } catch (err) {
      console.error(err);
      handleApiResponse(err);
    } finally {
      setPasswordLoading(false);
    }
  };

  const handleCloseOnboarding = () => {
    setShowOnboarding(false);
    localStorage.removeItem('isNewUserOnboarding');
  };

  const generateWebsiteUrl = (event) => {
    // Fallback only – real URL comes from API full_website_url
    if (!event || !event.event_title) return '#';
    const slug = event.event_title.toLowerCase().replace(/\s+/g, '');
    return `https://${slug}.harusi.online`;
  };

  const fallbackCopyText = (text) => {
    try {
      const textArea = document.createElement('textarea');
      textArea.value = text;
      textArea.style.position = 'fixed';
      document.body.appendChild(textArea);
      textArea.focus();
      textArea.select();
      const successful = document.execCommand('copy');
      document.body.removeChild(textArea);
      if (successful) {
        toast.success('Link copied to clipboard!');
      } else {
        toast.error('Imeshindwa kukopi link!');
      }
    } catch (err) {
      console.error('Fallback copy failed:', err);
      toast.error('Imeshindwa kukopi link!');
    }
  };

  const fetchDashboardData = async () => {
    setDashboardData(prev => ({ ...prev, loading: true, error: null }));

    try {
      // Fetch full_website_url from event settings
      try {
        const settingsResponse = await eventApi.getEventSettings();
        const settingsData = settingsResponse?.data?.data || settingsResponse?.data || {};
        const fullUrl = settingsData?.full_website_url;
        if (fullUrl) {
          setWebsiteUrl(fullUrl);
        } else {
          setWebsiteUrl(generateWebsiteUrl(currentEvent));
        }
      } catch (err) {
        console.warn('Could not fetch event settings URL:', err.message);
        setWebsiteUrl(generateWebsiteUrl(currentEvent));
      }

      // Fetch SMS and WhatsApp credits with error handling
      let smsCredits = 0;
      let smsUsed = 0;
      let whatsappCredits = 0;
      let whatsappUsed = 0;

      try {
        const creditsResponse = await smsApi.getEventCredits();
        let creditsData = creditsResponse?.data;
        if (Array.isArray(creditsData)) {
          creditsData = creditsData.length > 0 ? creditsData[0] : {};
        } else if (!creditsData) {
          creditsData = {};
        }

        if (creditsData && typeof creditsData === 'object') {
          smsCredits = creditsData.sms?.balance !== undefined ? creditsData.sms.balance : (creditsData.sms_balance || creditsData.credit_balance || creditsData.credits || 0);
          smsUsed = creditsData.sms?.used_credits !== undefined ? creditsData.sms.used_credits : (creditsData.sms_used || creditsData.credit_used || creditsData.used || 0);
          whatsappCredits = creditsData.whatsapp?.balance !== undefined ? creditsData.whatsapp.balance : (creditsData.whatsapp_balance || creditsData.whatsappCredits || 0);
          whatsappUsed = creditsData.whatsapp?.used_credits !== undefined ? creditsData.whatsapp.used_credits : (creditsData.whatsapp_used || creditsData.whatsappUsed || 0);
        }
      } catch (err) {
        console.log('Credits not available yet:', err.message);
      }

      // Fetch Event Statistics from /event-stats API
      let eventStats = {};
      try {
        const statsResponse = await eventApi.getEventStats();
        eventStats = statsResponse?.data?.data || statsResponse?.data || statsResponse || {};
      } catch (err) {
        console.log('Event stats error:', err.message);
      }

      const budgetVal = parseFloat(eventStats.budget) || 0;
      const pledgesVal = parseFloat(eventStats.pledges) || 0;
      const contributionsVal = parseFloat(eventStats.contributions) || 0;
      const expensesVal = parseFloat(eventStats.expenses) || 0;

      const guestsVal = parseInt(eventStats.guests) || 0;
      const pledgersVal = parseInt(eventStats.pledgers) || 0;
      const contributorsVal = parseInt(eventStats.contributors) || 0;
      const invitedVal = parseInt(eventStats.invited ?? eventStats.attendance_count) || 0;

      setDashboardData({
        totalPledges: pledgesVal,
        totalPledgesCount: pledgersVal,
        totalFunds: contributionsVal,
        totalFundsCount: contributorsVal,
        totalBudget: budgetVal,
        totalExpenditures: expensesVal,
        smsCredits: smsCredits || 0,
        smsUsed: smsUsed || 0,
        whatsappCredits: whatsappCredits || 0,
        whatsappUsed: whatsappUsed || 0,
        recentGuests: [],
        recentPledges: [],
        loading: false,
        error: null
      });

      setGuestStats({
        totalGuests: guestsVal,
        walioahidi: pledgersVal,
        waliochangia: contributorsVal,
        walioalikwa: invitedVal,
      });

    } catch (err) {
      console.error('Error fetching dashboard data:', err);
      setDashboardData(prev => ({
        ...prev,
        loading: false,
        error: null
      }));
    }
  };


  // Show loading state
  if (dashboardData.loading) {
    return (
      <div className="d-flex justify-content-center align-items-center" style={{ minHeight: '400px' }}>
        <div className="text-center">
          <FaSpinner className="fa-spin" size={48} color="#4169e1" />
          <p className="mt-3">Loading dashboard data...</p>
        </div>
      </div>
    );
  }

  // No error - always show dashboard with whatever data we have (even if empty)
  return (
    <>
      <div className="dashboard container mt-4">
        {/* Heading section */}
        <h1 className="stylish-heading text-center mb-4" style={{ color: '#4169e1' }}>
          Dashboard
        </h1>

        {/* Top Stats Section */}
        <div className="dashboard row bg-dash p-4 rounded-lg">

          {/* Bajeti / Wageni */}
          <div className="dashboard col text-center">
            <div className="text-dark">Bajeti</div>
            <div className="dashText" style={{ fontSize: '1.5rem' }}>
              TZS {dashboardData.totalBudget.toLocaleString()}
            </div>
            <div className="text-dark mt-2"><u>Wageni</u></div>
            <div className="dashText font-weight-bold" style={{ fontSize: '1.5rem' }}>
              {guestStats.totalGuests}
            </div>
          </div>

          {/* Ahadi / Walioahidi */}
          <div className="dashboard col text-center">
            <div className="text-dark">Ahadi</div>
            <div className="dashText" style={{ fontSize: '1.5rem' }}>
              TZS {dashboardData.totalPledges.toLocaleString()}
            </div>
            <div className="text-dark mt-2"><u>Walioahidi</u></div>
            <div className="dashText font-weight-bold" style={{ fontSize: '1.5rem' }}>
              {guestStats.walioahidi}
            </div>
          </div>

          {/* Michango / WalioChangia */}
          <div className="dashboard col text-center">
            <div className="text-dark">Michango</div>
            <div className="dashText" style={{ fontSize: '1.5rem' }}>
              TZS {dashboardData.totalFunds.toLocaleString()}
            </div>
            <div className="text-dark mt-2"><u>WalioChangia</u></div>
            <div className="dashText font-weight-bold" style={{ fontSize: '1.5rem' }}>
              {guestStats.waliochangia}
            </div>
          </div>

          {/* Matumizi / WalioAlikwa */}
          <div className="dashboard col text-center">
            <div className="text-dark">Matumizi</div>
            <div className="dashText" style={{ fontSize: '1.5rem' }}>
              TZS {dashboardData.totalExpenditures.toLocaleString()}
            </div>
            <div className="text-dark mt-2"><u>WalioAlikwa</u></div>
            <div className="dashText font-weight-bold" style={{ fontSize: '1.5rem' }}>
              {guestStats.walioalikwa}
            </div>
          </div>
        </div>

        {/* Free Wedding Website Promo Banner */}
        {currentEvent && (
          <div className="row mt-4 mb-2 px-3">
            <div
              className="d-flex flex-column flex-xl-row align-items-center p-2 px-3 rounded shadow-sm gap-2 text-center text-xl-start"
              style={{
                background: 'linear-gradient(135deg, #4a85ff 0%, #0666e0 100%)',
                color: '#fff',
              }}
            >
              <div className="d-flex align-items-center justify-content-center justify-content-xl-start gap-2 flex-grow-1" style={{ flexBasis: 0 }}>
                <FaGlobe size={16} style={{ opacity: 0.9 }} />
                <span className="fw-bold" style={{ fontSize: '0.95rem', letterSpacing: '0.3px', whiteSpace: 'nowrap' }}>
                  Your Free Wedding Website is Ready!
                </span>
              </div>

              <div className="d-flex align-items-center justify-content-center gap-2 flex-grow-1" style={{ flexBasis: 0 }}>
                <span
                  style={{
                    background: 'rgba(255,255,255,0.15)',
                    padding: '3px 10px',
                    borderRadius: '15px',
                    fontSize: '0.8rem',
                    fontFamily: 'monospace',
                    userSelect: 'all',
                    wordBreak: 'break-all'
                  }}
                >
                  {websiteUrl}
                </span>
                <button
                  className="btn border-0 d-flex align-items-center justify-content-center p-0"
                  style={{ background: 'rgba(255,255,255,0.2)', color: '#fff', borderRadius: '50%', width: '24px', height: '24px', flexShrink: 0 }}
                  onClick={(e) => {
                    e.preventDefault();
                    const url = websiteUrl;
                    if (navigator.clipboard && navigator.clipboard.writeText) {
                      navigator.clipboard.writeText(url)
                        .then(() => toast.success('Link copied to clipboard!'))
                        .catch(() => fallbackCopyText(url));
                    } else {
                      fallbackCopyText(url);
                    }
                  }}
                  title="Copy Link"
                >
                  <FaCopy size={12} />
                </button>
              </div>

              <div className="d-flex justify-content-center justify-content-xl-end flex-grow-1 mt-2 mt-xl-0" style={{ flexBasis: 0 }}>
                <a
                  href={websiteUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="btn btn-light fw-bold px-3 py-1 shadow-sm"
                  style={{ borderRadius: '20px', color: '#0666e0', fontSize: '0.85rem', whiteSpace: 'nowrap' }}
                >
                  View & Customize
                </a>
              </div>
            </div>
          </div>
        )}

        {/* Action Buttons Section */}
        <div className="dashboard row mt-4 align-items-stretch">
          <div className="dashboard col text-center d-flex flex-column mb-2 mb-md-0">
            <div className="text-decoration-none cursor-pointer h-100 d-flex flex-column" style={{ cursor: 'pointer' }} onClick={() => setShowTumaTaarifaModal(true)}>
              <div className="dashboard bg-dash action-card-btn p-3 rounded-lg h-100 d-flex flex-column align-items-center justify-content-center">
                <div className="dashText text-center">
                  <FaPaperPlane className="mb-2" size={24} />
                  <div className="fw-semibold" style={{ fontSize: '0.85rem', lineHeight: '1.25' }}>Tuma Taarifa</div>
                </div>
              </div>
            </div>
          </div>

          <div className="dashboard col text-center d-flex flex-column mb-2 mb-md-0">
            <div className="text-decoration-none cursor-pointer h-100 d-flex flex-column" style={{ cursor: 'pointer' }} onClick={() => setShowMichangoModal(true)}>
              <div className="dashboard bg-dash action-card-btn p-3 rounded-lg h-100 d-flex flex-column align-items-center justify-content-center">
                <div className="dashText text-center">
                  <FaPaperPlane className="mb-2" size={24} />
                  <div className="fw-semibold" style={{ fontSize: '0.85rem', lineHeight: '1.25' }}>Kumbusha Michango</div>
                </div>
              </div>
            </div>
          </div>

          <div className="dashboard col text-center d-flex flex-column mb-2 mb-md-0">
            <Link to="/ecards" className="text-decoration-none h-100 d-flex flex-column">
              <div className="dashboard bg-dash action-card-btn p-3 rounded-lg h-100 d-flex flex-column align-items-center justify-content-center">
                <div className="dashText text-center">
                  <FaEnvelopeOpenText className="mb-2" size={24} />
                  <div className="fw-semibold" style={{ fontSize: '0.85rem', lineHeight: '1.25' }}>E-cards</div>
                </div>
              </div>
            </Link>
          </div>

          <div className="dashboard col text-center d-flex flex-column mb-2 mb-md-0">
            <Link to="/sms-topup" className="text-decoration-none h-100 d-flex flex-column">
              <div className="dashboard bg-dash action-card-btn p-3 rounded-lg h-100 d-flex flex-column align-items-center justify-content-center">
                <div className="dashText text-center">
                  <FaPaperPlane className="mb-2" size={24} />
                  <div className="fw-semibold" style={{ fontSize: '0.85rem', lineHeight: '1.25' }}>Ongeza Message</div>
                </div>
              </div>
            </Link>
          </div>

          <div className="dashboard col text-center d-flex flex-column mb-2 mb-md-0">
            <div className="text-decoration-none cursor-pointer h-100 d-flex flex-column" style={{ cursor: 'pointer' }} onClick={() => setShowQuickReportModal(true)}>
              <div
                className="dashboard bg-dash action-card-btn p-3 rounded-lg h-100 d-flex flex-column align-items-center justify-content-center"
                style={{
                  background: 'linear-gradient(135deg, #ecfdf5 0%, #d1fae5 100%)',
                  border: '1px solid #6ee7b7'
                }}
              >
                <div className="dashText dashText-green text-center">
                  <FaWhatsapp className="mb-2" size={24} />
                  <div className="fw-semibold" style={{ fontSize: '0.85rem', lineHeight: '1.25' }}>Share Summary</div>
                </div>
              </div>
            </div>
          </div>
        </div>
        <div className="mt-5">
          <Wageni isDashboard={true} />
        </div>

        {/* External Modals */}
        <QuickReportModal
          show={showQuickReportModal}
          onHide={() => setShowQuickReportModal(false)}
          dashboardData={dashboardData}
          currentEvent={currentEvent}
          guestsData={guestsData}
        />

        <KumbushaMichangoModal
          show={showMichangoModal}
          onHide={() => setShowMichangoModal(false)}
        />

        <TumaTaarifaModal
          show={showTumaTaarifaModal}
          onHide={() => setShowTumaTaarifaModal(false)}
        />

        {/* Onboarding Tour Modal */}
        <OnboardingModal
          show={showOnboarding}
          onHide={handleCloseOnboarding}
        />

        {/* Change Password Modal */}
        <Modal
          show={showChangePasswordModal}
          onHide={() => { }}
          backdrop="static"
          keyboard={false}
          centered
          contentClassName="border-0 shadow-lg"
        >
          <Modal.Header className="border-0 justify-content-center pt-4">
            <Modal.Title className="fw-bold" style={{ color: '#4169e1', fontSize: '1.4rem' }}>Tengeneza Password Mpya</Modal.Title>
          </Modal.Header>
          <Modal.Body className="px-4 pb-4">
            <Form onSubmit={handleSaveNewPassword}>
              <p className="text-muted text-center mb-4" style={{ fontSize: '0.9rem' }}>
                Tafadhali weka password mpya ya akaunti yako ili kuendelea kuingia kwenye mfumo.
              </p>
              {passwordError && <div className="alert alert-danger py-2" style={{ fontSize: '0.85rem' }}>{passwordError}</div>}
              <Form.Group className="mb-3">
                <Form.Label className="fw-semibold small">Password Mpya</Form.Label>
                <Form.Control
                  type="password"
                  placeholder="Weka password mpya (herufi 6 au zaidi)"
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  style={{ borderRadius: '8px', fontSize: '0.9rem' }}
                  required
                />
              </Form.Group>
              <Form.Group className="mb-4">
                <Form.Label className="fw-semibold small">Thibitisha Password Mpya</Form.Label>
                <Form.Control
                  type="password"
                  placeholder="Rudia password mpya"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  style={{ borderRadius: '8px', fontSize: '0.9rem' }}
                  required
                />
              </Form.Group>
              <Button
                type="submit"
                style={{
                  backgroundColor: '#4169e1',
                  border: 'none',
                  borderRadius: '25px',
                  padding: '10px 20px',
                  fontWeight: '600',
                  width: '100%',
                  boxShadow: '0 4px 12px rgba(65, 105, 225, 0.2)'
                }}
                disabled={passwordLoading}
              >
                {passwordLoading ? (
                  <span><i className="fa fa-spinner fa-spin me-2"></i>Inahifadhi...</span>
                ) : 'Hifadhi Password'}
              </Button>
            </Form>
          </Modal.Body>
        </Modal>

        {/* Event Selection Modal */}
        <Modal show={showEventSelectorModal} onHide={() => setShowEventSelectorModal(false)} centered size="md">
          <Modal.Header closeButton className="border-0 pb-0">
            <Modal.Title className="w-100 text-center fw-bold" style={{ color: '#4169e1', fontSize: '1.4rem' }}>
              Karibu !
            </Modal.Title>
          </Modal.Header>
          <Modal.Body className="p-4 text-center">
            <p className="text-muted mb-3" style={{ fontSize: '0.92rem' }}>
              Kuendelea chagua shughuli unayotaka kusimamia au tengeneza shughuli mpya hapa chini (Ikiwa ni tofauti na zilizopo hapa).
            </p>

            <div className="d-flex flex-column gap-2 mb-4" style={{ maxHeight: '280px', overflowY: 'auto' }}>
              {Array.isArray(events) && events.length > 0 ? (
                events.map((evt, idx) => {
                  const isActive = currentEvent && (currentEvent.id === evt.id || currentEvent.event_address === evt.event_address);
                  return (
                    <div
                      key={evt.id || idx}
                      className={`p-3 rounded border text-start d-flex align-items-center justify-content-between ${isActive ? 'border-primary bg-light' : ''}`}
                      style={{
                        backgroundColor: isActive ? '#eff6ff' : '#f8fafc',
                        borderColor: isActive ? '#3b82f6' : '#cbd5e1',
                        cursor: 'pointer',
                        transition: 'all 0.2s'
                      }}
                      onClick={() => {
                        if (switchEvent) switchEvent(evt);
                        setShowEventSelectorModal(false);
                      }}
                    >
                      <div>
                        <div className="fw-bold" style={{ color: '#1e293b', fontSize: '0.98rem' }}>
                          {evt.event_title || evt.name || 'Shughuli'}
                          {isActive && <span className="badge bg-primary ms-2" style={{ fontSize: '0.7rem' }}>Inatumika</span>}
                        </div>
                        <div className="text-muted" style={{ fontSize: '0.8rem' }}>
                          {evt.event_type} {evt.event_date ? `• ${new Date(evt.event_date).toLocaleDateString()}` : ''}
                        </div>
                      </div>
                      <Button variant={isActive ? "primary" : "outline-primary"} size="sm" style={{ borderRadius: '20px', fontSize: '0.8rem' }}>
                        {isActive ? "Fungua" : "Chagua"}
                      </Button>
                    </div>
                  );
                })
              ) : (
                <p className="text-muted my-3">Hakuna shughuli zilizopatikana.</p>
              )}
            </div>

            <div className="border-top pt-3">
              <Button
                variant="success"
                className="w-100 py-2 fw-bold"
                style={{ borderRadius: '25px', backgroundColor: '#10b981', border: 'none' }}
                onClick={() => {
                  setShowEventSelectorModal(false);
                  setShowCreateEventModal(true);
                }}
              >
                + Tengeneza Shughuli Mpya
              </Button>
            </div>
          </Modal.Body>
        </Modal>

        {/* Create Event Modal */}
        <CreateEventModal
          show={showCreateEventModal}
          handleClose={() => setShowCreateEventModal(false)}
        />
      </div>
    </>
  );
};


export default Dashboard;
